import * as React from 'react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useCreateSubscription } from '../api/create-subscription'
import { useEditSubscription } from '../api/edit-subscription'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form } from '@/components/ui/form'
import TextField from '@/components/forms/text-field'
import CalendarField from '@/components/forms/calendar-field'
import CalendarRangeField from '@/components/forms/calendar-range-field'
import AccountCombobox from '@/features/accounts/components/AccountCombobox'
import StoresCombobox from '@/features/accounts/components/StoresCombobox'
import SelectField from '@/components/forms/select-field'
import PlanCombobox from '@/features/plans/components/PlanCombobox'
import ChargebeeSubscriptionsCombobox from '@/features/subscriptions/components/ChargebeeSubscriptionsCombobox'
import SwitchField from '@/components/forms/switch-field'
import { usePlan } from '@/features/plans/api/get-plan'
import { usePlans } from '@/features/plans/api/get-plans'

type SubscriptionItem = {
    subscriptionItemId?: string
    planId: string
    plan?: {
        planId: string
        planSku: string
        name: string
        description: string
        billingInterval: string
        isAddon: boolean
        createdAt: string
        updatedAt: string
        features: any
    } | null
    quantity: number
    isBasePlan: boolean
    metered: boolean
}

type SubscriptionFormValues = {
    subscriptionId?: string
    accountId: string
    scope: string
    storeId: string
    source: string
    partnerId: string | null
    chargebeeSubscriptionId: string | null
    status: string
    primaryFlag: boolean
    startAt: string | null
    currentPeriodStart: string | null
    currentPeriodEnd: string | null
    renewalAt: string | null
    trialEndAt: string | null
    cancelAt: string | null
    canceledAt: string | null
    createdAt?: string
    updatedAt?: string
    items: SubscriptionItem[]
    account?: {
        accountId: string
        email: string
        name: string
        phone: string
        state: string
        city: string
        address1: string
        address2: string | null
        createdAt: string
        activationDate: string
    }
}

type SubscriptionFormProps = {
    // Accept any shape for prefill to avoid breaking callers while migrating
    subscription?: any
}

export default function SubscriptionForm({ subscription }: SubscriptionFormProps) {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const accountIdFromUrl = searchParams.get('accountId') || ''
    
    const form = useForm<SubscriptionFormValues>({
        defaultValues: subscription ? {
            subscriptionId: subscription.subscriptionId ?? subscription.id ?? '',
            accountId: subscription.accountId ?? '',
            scope: subscription.scope ?? 'account',
            storeId: subscription.storeId ?? '',
            source: subscription.source ?? '',
            partnerId: subscription.partnerId ?? null,
            chargebeeSubscriptionId: subscription.chargebeeSubscriptionId ?? null,
            status: subscription.status ?? 'active',
            primaryFlag: !!subscription.primaryFlag,
            startAt: subscription.startAt ?? null,
            currentPeriodStart: subscription.currentPeriodStart ?? null,
            currentPeriodEnd: subscription.currentPeriodEnd ?? null,
            renewalAt: subscription.renewalAt ?? null,
            trialEndAt: subscription.trialEndAt ?? null,
            cancelAt: subscription.cancelAt ?? null,
            canceledAt: subscription.canceledAt ?? null,
            createdAt: subscription.createdAt,
            updatedAt: subscription.updatedAt,
            items: Array.isArray(subscription.items) && subscription.items.length > 0 ? [
                {
                    subscriptionItemId: subscription.items[0].subscriptionItemId,
                    planId: subscription.items[0].planId ?? '',
                    plan: subscription.items[0].plan ?? null,
                    quantity: subscription.items[0].quantity ?? 1,
                    isBasePlan: !!subscription.items[0].isBasePlan,
                    metered: !!subscription.items[0].metered,
                }
            ] : [
                { planId: '', quantity: 1, isBasePlan: true, metered: false }
            ],
            account: subscription.account,
        } : {
            subscriptionId: '',
            accountId: accountIdFromUrl,
            scope: 'account',
            storeId: '',
            source: 'direct',
            partnerId: null,
            chargebeeSubscriptionId: null,
            status: 'active',
            primaryFlag: false,
            startAt: null,
            currentPeriodStart: null,
            currentPeriodEnd: null,
            renewalAt: null,
            trialEndAt: null,
            cancelAt: null,
            canceledAt: null,
            items: [{ planId: '', quantity: 1, isBasePlan: true, metered: false }],
        }
    })
    React.useEffect(() => {
        // Ensure scope has a default
        const current = form.getValues('scope' as any) as any
        if (!current) form.setValue('scope' as any, 'account', { shouldDirty: false })
        
        // Pre-fill accountId from URL if not already set
        if (!subscription && accountIdFromUrl) {
            const currentAccountId = form.getValues('accountId' as any) as any
            if (!currentAccountId) {
                form.setValue('accountId' as any, accountIdFromUrl, { shouldDirty: false })
            }
        }
    }, [accountIdFromUrl, subscription, form])


    const createMutation = useCreateSubscription()
    const editMutation = useEditSubscription()
    const { register, handleSubmit, formState: { isSubmitting } } = form
    
    // Fetch plans to get plan details
    const { data: plansData } = usePlans({})
    const plans = (plansData as unknown as any[]) ?? []
    
    // Helper to convert empty strings to null
    const toNullIfEmpty = (value: string | null | undefined): string | null => {
        if (value === '' || value === undefined) return null
        return value
    }
    
    // Helper to get plan details by planId
    const getPlanDetails = (planId: string) => {
        return plans.find((p) => (p.planId ?? p.id) === planId)
    }
    
    const onSubmit = (values: SubscriptionFormValues) => {
        const id = values.subscriptionId ?? subscription?.subscriptionId ?? subscription?.id
        
        if (id) {
            // Edit: keep the nested structure for edit endpoint
            const payload = {
                subscription: {
                    subscriptionId: values.subscriptionId,
                    accountId: values.accountId,
                    scope: values.scope,
                    storeId: toNullIfEmpty(values.storeId),
                    source: values.source,
                    partnerId: toNullIfEmpty(values.partnerId),
                    chargebeeSubscriptionId: toNullIfEmpty(values.chargebeeSubscriptionId),
                    status: values.status,
                    primaryFlag: Boolean(values.primaryFlag),
                    startAt: toNullIfEmpty(values.startAt),
                    currentPeriodStart: toNullIfEmpty(values.currentPeriodStart),
                    currentPeriodEnd: toNullIfEmpty(values.currentPeriodEnd),
                    renewalAt: toNullIfEmpty(values.renewalAt),
                    trialEndAt: toNullIfEmpty(values.trialEndAt),
                    cancelAt: toNullIfEmpty(values.cancelAt),
                    canceledAt: toNullIfEmpty(values.canceledAt),
                },
                items: (values.items || []).map((it) => ({
                    planId: it.planId,
                    quantity: it.quantity,
                    isBasePlan: it.isBasePlan,
                    metered: it.metered,
                })),
            }
            editMutation.mutate({ id, ...payload }, {
                onSuccess: () => navigate('/subscriptions'),
            })
        } else {
            // Create: use flat structure with null for empty values
            // Include plan details if planId is provided
            const firstItem = values.items?.[0]
            const planDetails = firstItem?.planId ? getPlanDetails(firstItem.planId) : null
            
            const payload: any = {
                accountId: values.accountId,
                scope: values.scope,
                storeId: toNullIfEmpty(values.storeId),
                source: values.source,
                partnerId: toNullIfEmpty(values.partnerId),
                chargebeeSubscriptionId: toNullIfEmpty(values.chargebeeSubscriptionId),
                status: values.status,
                primaryFlag: Boolean(values.primaryFlag),
                startAt: toNullIfEmpty(values.startAt),
                currentPeriodStart: toNullIfEmpty(values.currentPeriodStart),
                currentPeriodEnd: toNullIfEmpty(values.currentPeriodEnd),
                renewalAt: toNullIfEmpty(values.renewalAt),
                trialEndAt: toNullIfEmpty(values.trialEndAt),
                cancelAt: toNullIfEmpty(values.cancelAt),
                canceledAt: toNullIfEmpty(values.canceledAt),
                items: (values.items || []).map((it) => ({
                    planId: it.planId,
                    quantity: it.quantity,
                    isBasePlan: it.isBasePlan,
                    metered: it.metered,
                })),
            }
            
            // Add plan details if available (API might require these fields)
            if (planDetails) {
                payload.Name = planDetails.name ?? ''
                payload.MaxFFLs = planDetails.maxFFLs ?? 0
                payload.MaxDocuments = planDetails.maxDocuments ?? 0
                payload.PricePerMonth = planDetails.pricePerMonth ?? 0
            }
            
            createMutation.mutate(payload, {
                onSuccess: () => navigate('/subscriptions'),
            })
        }
    }

    return (
        <div className='bg-white rounded-xl p-4 max-w-xl'>
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 max-w-xl'>
                    <SelectField
                        label='Scope'
                        name='scope'
                        options={[
                            { label: 'Account', value: 'account' },
                            { label: 'Store', value: 'store' },
                        ]}
                        placeholder='Select scope'
                        helpText='The scope of the subscription. This determines whether the subscription is associated with an account or a store.'
                    />
                    <AccountCombobox label='Account' name='accountId' />

                    {form.watch('scope' as any) === 'store' && (
                        <StoresCombobox accountId={form.watch('accountId' as any) as string} name='storeId' />
                    )}
                    <PlanCombobox label='Plan' name='items.0.planId' />
                    <TextField label='Source' name='source' />
                    <SelectField
                        label='Status'
                        name='status'
                        placeholder='Select status'
                        options={[
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                        ]}
                    />
                    <div className='mt-6 border-t pt-4 space-y-3'>
                        <div className='text-sm font-medium text-muted-foreground'>Integrations</div>
                        <TextField label='Partner ID' name='partnerId' />
                        <ChargebeeSubscriptionsCombobox name='chargebeeSubscriptionId' label='Chargebee Subscription' />
                    </div>
                    <SwitchField label='Primary Flag' name='primaryFlag' />

                    <div className='mt-6 border-t pt-4 space-y-3'>
                        <div className='text-sm font-medium text-muted-foreground'>Scheduling</div>
                        <CalendarField label='Start At' name='startAt' />
                        <CalendarRangeField label='Current Period' nameFrom='currentPeriodStart' nameTo='currentPeriodEnd' />
                        <CalendarField label='Renewal At' name='renewalAt' />
                        <CalendarField label='Trial End At' name='trialEndAt' />
                        <CalendarField label='Cancel At' name='cancelAt' />
                        <CalendarField label='Canceled At' name='canceledAt' />
                    </div>

                    <div className='mt-6 border-t pt-4 space-y-3'>
                        <div className='grid gap-2'>
                            <Label htmlFor='items.0.quantity'>Quantity</Label>
                            <Input id='items.0.quantity' type='number' {...register('items.0.quantity', { valueAsNumber: true })} />
                        </div>
                        <div className='flex items-center gap-2'>
                            <input id='items.0.isBasePlan' type='checkbox' {...register('items.0.isBasePlan')} />
                            <Label htmlFor='items.0.isBasePlan'>Is Base Plan</Label>
                        </div>
                        <div className='flex items-center gap-2'>
                            <input id='items.0.metered' type='checkbox' {...register('items.0.metered')} />
                            <Label htmlFor='items.0.metered'>Metered</Label>
                        </div>
                    </div>

                    <Button type='submit' disabled={isSubmitting || createMutation.isPending || editMutation.isPending}>
                        {createMutation.isPending || editMutation.isPending ? 'Saving…' : (form.getValues('subscriptionId' as any) ? 'Save changes' : 'Create subscription')}
                    </Button>
                    {(createMutation.isError || editMutation.isError) && (
                        <div className='text-destructive'>Failed to create subscription.</div>
                    )}
                    {createMutation.isSuccess && (
                        <div className='text-green-600'>Subscription created.</div>
                    )}
                    {editMutation.isSuccess && (
                        <div className='text-green-600'>Subscription updated.</div>
                    )}
                </form>
            </Form>
        </div>
    )
}


